import { _ as __rest } from './_tslib.js';
import { jsxs, jsx } from 'react/jsx-runtime';
import { enforceNumber, generateClasses, platform } from '@ids/tools';
import { useMemo } from 'react';
import { s as styleInject } from './style-inject.es.js';

var css_248z = ".ids-pagination-number {\n  display: -webkit-box;\n  display: -ms-flexbox;\n  display: flex;\n}\n.ids-pagination-number__container {\n  -webkit-box-align: center;\n      -ms-flex-align: center;\n          align-items: center;\n  display: -webkit-box;\n  display: -ms-flexbox;\n  display: flex;\n  gap: var(--ids_size_general_8x);\n}\n.ids-pagination-number__label {\n  color: var(--ids_color_action_neutral_base);\n  font: var(--ids_textStyle_f02_s12_h18_wrg);\n}\n.ids-pagination-number__label--bold {\n  font: var(--ids_textStyle_f02_s12_h18_wbd);\n}\n.ids-pagination-number__progress-bar {\n  -ms-flex-item-align: center;\n      align-self: center;\n  background: -webkit-gradient(linear, left top, right top, from(var(--ids_color_action_neutral_variant)), to(var(--ids_color_action_neutral_variant)));\n  background: linear-gradient(to right, var(--ids_color_action_neutral_variant) 0%, var(--ids_color_action_neutral_variant) 100%);\n  background-color: var(--ids_color_action_neutral_base_soft);\n  background-repeat: no-repeat;\n  border-radius: var(--ids_size_general_4x);\n  height: var(--ids_size_general_4x);\n  -webkit-transition: background-size 175ms ease-in;\n  transition: background-size 175ms ease-in;\n  width: var(--ids_size_general_32x);\n}\n.ids-pagination-number--on-brand .ids-pagination-number__label {\n  color: var(--ids_color_contrast_onBrand_primary);\n}\n.ids-pagination-number--on-brand .ids-pagination-number__progress-bar {\n  background: -webkit-gradient(linear, left top, right top, from(var(--ids_color_contrast_onBrand_primary)), to(var(--ids_color_contrast_onBrand_primary)));\n  background: linear-gradient(to right, var(--ids_color_contrast_onBrand_primary) 0%, var(--ids_color_contrast_onBrand_primary) 100%);\n  background-color: var(--ids_color_contrast_onBrand_primary_soft);\n  background-repeat: no-repeat;\n}";
styleInject(css_248z);

/**
 * @category Component
 *
 * @example
 * ```ts
 * import { IdsPaginationNumber } from '@ids/react';
 * ```
 */
const IdsPaginationNumber = _a => {
  var {
      contrast,
      current: externalCurrent = 1,
      steps: externalSteps = 1,
      className = '',
      'aria-label': externalAriaLabel,
      context = 'Item'
    } = _a,
    props = __rest(_a, ["contrast", "current", "steps", "className", 'aria-label', "context"]);
  const current = enforceNumber(externalCurrent);
  const steps = enforceNumber(externalSteps);
  const fill = 100 * current / steps;
  const classes = useMemo(() => generateClasses({
    'ids-pagination-number': true,
    'ids-pagination-number--on-brand': contrast === 'onBrand',
    [className]: className
  }), [contrast, className]);
  const numberRightClasses = useMemo(() => generateClasses({
    'ids-pagination-number__label': true,
    'ids-pagination-number__label--bold': fill === 100
  }), [fill]);
  const role = platform.os === 'Android' || platform.os === 'IOS' ? 'progressbar' : 'img';
  const ariaValueNow = role !== 'img' ? current : undefined;
  const ariaValuemax = role !== 'img' ? steps : undefined;
  const ariaValuetext = role !== 'img' ? context + ' ' + current + ' selecionado.' : undefined;
  const ariaLabel = externalAriaLabel || `${context} ${current} de ${steps}. O progresso é de ${Math.round(fill)}%`;
  return jsxs("div", Object.assign({
    className: classes,
    "aria-label": ariaLabel,
    "aria-valuenow": ariaValueNow,
    "aria-valuemax": ariaValuemax,
    "aria-valuetext": ariaValuetext,
    role: role
  }, props, {
    children: [jsx("span", Object.assign({
      className: "ids-pagination-number__label ids-pagination-number__label--bold"
    }, {
      children: current
    })), jsx("div", {
      className: "ids-pagination-number__progress-bar",
      style: {
        backgroundSize: `${fill}% 100%`
      }
    }), jsx("span", Object.assign({
      className: numberRightClasses
    }, {
      children: steps
    }))]
  }));
};

export { IdsPaginationNumber as I };
//# sourceMappingURL=pagination-number.js.map
